import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';

import { Button, Modal } from '@/components';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import { SiSchedule, scheduledInstantAPI } from '@/services/api/scheduled-instant';

interface DeactivateScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  schedule: SiSchedule | null;
  onDone: () => void;
}

const labelCls =
  'mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500';
const inputCls =
  'w-full rounded-gsm border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500';

const DeactivateScheduleModal: React.FC<DeactivateScheduleModalProps> = ({
  isOpen,
  onClose,
  schedule,
  onDone,
}) => {
  const [effectiveDate, setEffectiveDate] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setEffectiveDate(dayjs().format('YYYY-MM-DD'));
      setReason('');
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!schedule) return;
    if (!reason.trim()) {
      showErrorToast('Belum lengkap', 'Alasan wajib diisi.');
      return;
    }
    setSubmitting(true);
    try {
      await scheduledInstantAPI.deactivateSchedule(schedule.id, {
        effectiveDate,
        reason: reason.trim(),
      });
      showSuccessToast('Jadwal dinonaktifkan', schedule.clientName);
      onDone();
      onClose();
    } catch (e: any) {
      showErrorToast('Gagal', e.message || 'Terjadi kesalahan');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !submitting && onClose()}
      icon="ri-pause-circle-line"
      iconTone="warning"
      title="Nonaktifkan Jadwal"
      subtitle={
        schedule
          ? `${schedule.clientName} · ${schedule.windowStartTime}–${schedule.windowEndTime}`
          : undefined
      }
      width="sm"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="white" onClick={onClose} disabled={submitting}>
            Batal
          </Button>
          <Button
            variant="danger"
            onClick={handleSubmit}
            disabled={submitting || !reason.trim()}
          >
            {submitting ? 'Menyimpan…' : 'Nonaktifkan'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-gray-500 leading-relaxed">
          Generasi slot harian berhenti mulai tanggal efektif. Slot yang sudah
          ada tetap ada — nonaktifkan hanya menghentikan pembuatan slot baru.
        </p>
        <div>
          <label htmlFor="deact-date" className={labelCls}>
            Tanggal Efektif <span className="text-red-500">*</span>
          </label>
          <input
            id="deact-date"
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            className={`${inputCls} font-mono`}
            disabled={submitting}
          />
        </div>
        <div>
          <label htmlFor="deact-reason" className={labelCls}>
            Alasan <span className="text-red-500">*</span>
          </label>
          <input
            id="deact-reason"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="cth. Kontrak klien selesai"
            className={inputCls}
            disabled={submitting}
          />
        </div>
      </div>
    </Modal>
  );
};

export default DeactivateScheduleModal;
